<script lang="ts" setup>
import OverlayWithDialog from '@/ui/common/overlay/OverlayWithDialog.vue'
import TextView from '@/ui/common/text-view/TextView.vue'
import { useI18n } from 'vue-i18n'
import { Button, Select } from 'ant-design-vue'
import { ref } from 'vue'
import type { TExportFormat } from '@/api/modules/message/types'
import {useEscapeKey} from "@/composables/use-escape-key";

const { t } = useI18n()

const exportFormat = ref<TExportFormat>('txt')

interface Props {
  isOpen: boolean
  isLoading?: boolean
}

interface Emits {
  (e: 'close'): void
  (e: 'export', format: TExportFormat): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const handleExport = () => {
  emit('export', exportFormat.value)
}

const handleClose = () => {
  emit('close')
  exportFormat.value = 'txt' // Сбрасываем значение при закрытии
}

const formatOptions = [
  { value: 'txt', label: 'TXT' },
  { value: 'md', label: 'Markdown' },
  { value: 'pdf', label: 'PDF' }
]

useEscapeKey(handleClose, !props.isLoading)

</script>

<template>
  <OverlayWithDialog :is-open="isOpen" @close="handleClose">
    <div class="export-dialog">
      <TextView variant="roboto_18_bold" class="export-dialog__title">
        {{ t('dialogs.exportThread.title') }}
      </TextView>

      <div class="export-dialog__content">
        <TextView variant="roboto_13_regular" class="export-dialog__label">
          {{ t('dialogs.exportThread.selectFormat') }}
        </TextView>

        <Select
            v-model:value="exportFormat"
            :options="formatOptions"
            class="export-dialog__select"
            :disabled="isLoading"
            popupClassName="export-dialog__select-dropdown"
        />
      </div>

      <div class="export-dialog__actions">
        <Button
            :disabled="isLoading"
            @click="handleClose"
            class="export-dialog__button export-dialog__button--cancel"
        >
          <TextView variant="roboto_13_regular">
            {{ t('dialogs.exportThread.cancel') }}
          </TextView>
        </Button>

        <Button
            :disabled="isLoading"
            :loading="isLoading"
            @click="handleExport"
            class="export-dialog__button export-dialog__button--export"
        >
          <TextView variant="roboto_13_regular">
            {{ t('dialogs.exportThread.export') }}
          </TextView>
        </Button>
      </div>
    </div>
  </OverlayWithDialog>
</template>

<style lang="scss" scoped>
.export-dialog {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 400px;
  padding: 4px;
  z-index: 1000;


  &__title {
    color: var(--gray_90);
    text-align: center;
    margin: 0;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__label {
    color: var(--gray_70);
    margin: 0;
  }

  &__select {
    width: 100%;

    :deep(.ant-select-selector) {
      height: 36px;
      border-radius: 6px;
      border-color: var(--gray_30);

      &:hover {
        border-color: var(--gray_40);
      }
    }

    :deep(.ant-select-selection-item) {
      display: flex;
      align-items: center;
      height: 100%;
    }
  }

  &__actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 8px;
  }

  &__button {
    min-width: 80px;
    height: 32px;
    padding: 4px 16px;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    :deep(.ant-btn) {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &--cancel {
      background: var(--gray_10);
      border: 1px solid var(--gray_20);

      &:hover:not(:disabled) {
        background: var(--gray_20);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    &--export {
      background: var(--primary_50);
      border: 1px solid var(--primary_50);
      color: var(--white);

      &:hover:not(:disabled) {
        background: var(--primary_60);
        border-color: var(--primary_60);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}

:global(.export-dialog__select-dropdown) {
  z-index: 2147483647 !important;
}

:global(.ant-select-dropdown) {
  z-index: 2147483647 !important;
}
</style>